import React, { Component, use } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import TicketGuardianWrapper from '../components/TicketGuardianWrapper';
import ProtectWrapper from '../components/ProtectWrapper';
import { getInsuranceQuote, setInsurancePolicy, setInsuranceOption, setInsurancePrice } from 'checkout/views/insurance/actions/insurance';
import { createLoadingSelector } from 'checkout/selectors/helpers';
import { feesSlice } from '../../../slices/fees';
import { reservationQualifiesForInsurance } from 'checkout/selectors/reservation';
import { submitInsuranceOption } from 'checkout/views/insurance/actions/insurance';
import { toMinorUnit } from '@h/currency';
import { updateStripePaymentIntent } from '../../../actions/payment';
import { hasOverMaxProtectedDays } from '@b/checkout/selectors/ui';
import {getSelectedEvent} from "@/selectors/event";

class InsuranceContainer extends Component {
  state = {
    hasSubmittedInsurance: false,
    componentKey: 0, // Key to force component remount
  };

  async componentDidUpdate(prevProps) {
    if (prevProps.price !== null && prevProps.price !== this.props.price && !this.state.hasSubmittedInsurance) {
      this.setState({ hasSubmittedInsurance: true });
      const result = await this.props.submitInsuranceOption();

      if (!result) {
        this.resetComponent();
      }
    } else if (prevProps.amount !== this.props.amount && (Math.abs(this.props.amount - prevProps.amount) != prevProps.priceQuote)) {
      this.resetComponent();
    }
  }

  resetComponent = () => {
    this.setState(prevState => ({
      componentKey: prevState.componentKey + 1,
      hasSubmittedInsurance: false,
    }));
  };

  render() {
    if (!this.props.reservationQualifiesForInsurance || this.props.noReservation) {
      return null;
    }

    // Wrap using key to force remount when it changes
    return <div key={this.state.componentKey}>{this.loadInsurance()}</div>;
  }

  loadInsurance = () => {
    if (this.props.provider === 'ticketguardian') {
      return (
        <TicketGuardianWrapper
          selected={this.props.selected === 'ticketguardian'}
          price={this.props.price}
          loading={this.props.tgLoading}
          onInsuranceChange={this.onInsuranceChange}
          declined={this.props.declined}
          widget_api_key={this.props.widget_api_key}
          version={this.props.version}
          sandbox={this.props.sandbox}
          templateId={this.props.templateId}
          insuranceItems={this.props.insuranceItems}
          currency={this.props.currency}
          locale={this.props.locale}
          setInsuranceOption={this.props.setInsuranceOption}
        />
      );
    } else if (this.props.provider === 'protect') {
      if (this.props.hasOverMaxProtectedDays) {
        return null;
      }

      return (
        <ProtectWrapper
          selected={this.props.selected === 'protect'}
          price={this.props.price}
          loading={this.props.tgLoading}
          onInsuranceChange={this.onInsuranceChange}
          declined={this.props.declined}
          widget_api_key={this.props.widget_api_key}
          version={this.props.version}
          sandbox={this.props.sandbox}
          templateId={this.props.templateId}
          insuranceItems={this.props.insuranceItems}
          currency={this.props.currency}
          locale={this.props.locale}
          setInsuranceOption={this.props.setInsuranceOption}
          mainEvent={this.props.event}
          mainVenue={this.props.venue}
          reservation={this.props.reservation}
          seller_id={this.props.seller_id}
          seller_organization={this.props.seller_organization}
        />
      );
    }
  };

  onInsuranceChange = async (val, updatedPrice, quoteToken = '', quoteHash = '') => {
    if (this.props.loading) {
      return;
    }

    this.setState({ hasSubmittedInsurance: false });

    this.props.setInsurancePolicy(val, { quoteToken, quoteHash });
    this.updateInsuranceCost(val, updatedPrice);
    this.props.setInsurancePrice(updatedPrice || 0);

    // having quote token or hash set means the user had selected an option
    if (!this.state.hasSubmittedInsurance && quoteToken != ''){
      const result = await this.props.submitInsuranceOption();
      if (!result) {
        this.props.setInsurancePolicy('', { quoteToken: '', quoteHash: '' });
        this.props.setInsurancePrice(0);
        this.updateInsuranceCost('', 0);
        this.resetComponent();
      }
    }
  };

  updateInsuranceCost = (val, updatedPrice) => {
    if (val !== '') {
      if (updatedPrice != null) {
        this.props.setFee({ type: 'insurance', id: null, price: { amount: updatedPrice } });
      } else {
        this.props.setFee({ type: 'insurance', id: null, price: { amount: this.props.price } });
      }
    } else {
      this.props.setFee({ type: 'insurance', id: null, price: { amount: 0 } });
    }
  };
}

const tgLoading = createLoadingSelector('TICKETGUARDIAN_QUOTE');
const mapStateToProps = (state) => {
  if (!state.reservation.total_cost) {
    return {
      noReservation: true,
    };
  }

  const minimumOrderValue = state.settings.refundAsAServiceMinimumOrderValue;

  if (
    minimumOrderValue &&
    state.reservation.ticket_total.amount < toMinorUnit(minimumOrderValue, state.reservation.ticket_total.currency)
  ) {
    return {
      reservationQualifiesForInsurance: false,
    };
  }
  const selectedEvent = getSelectedEvent(state);
  return {
    provider: state.insurance.provider,
    selected: state.insurance.selected,
    total: state.reservation.total,
    price: state.insurance.price,
    priceQuote: state.insurance.priceQuote,
    tgLoading: tgLoading(state),
    declined: state.insurance.declined,
    widget_api_key: state.insurance.widget_api_key,
    version: state.insurance.version,
    sandbox: state.insurance.sandbox,
    templateId: state.insurance.templateId,
    insuranceItems: state.insurance.insuranceItems,
    currency: state.reservation && state.reservation.total_cost ? state.reservation.total_cost.currency : null,
    reservationQualifiesForInsurance: reservationQualifiesForInsurance(state),
    locale: state.intl.locale,
    amount: state.reservation.total_cost ? state.reservation.total_cost.amount : 0,
    paymentIntentSecret: state.payment.stripePaymentIntentSecret,
    paymentIntentId: state.payment.stripePaymentIntentId,
    event: selectedEvent.event,
    venue: Object.values(state.venue.venues)[0],
    reservation: state.reservation,
    seller_id: selectedEvent.seller_id,
    seller_organization: selectedEvent.seller_organization,
    hasOverMaxProtectedDays: hasOverMaxProtectedDays(state),
  };
};

const mapDispatchToProps = (dispatch) => {
  return bindActionCreators(
    {
      getInsuranceQuote,
      setInsurancePolicy,
      setInsurancePrice,
      setInsuranceOption,
      setFee: feesSlice.actions.setFee,
      submitInsuranceOption,
      updateStripePaymentIntent,
    },
    dispatch,
  );
};

InsuranceContainer.propTypes = {
  provider: PropTypes.string,
  selected: PropTypes.string,
  price: PropTypes.number,
  total: PropTypes.number,
  version: PropTypes.string,
};

InsuranceContainer.defaultProps = {
  provider: '',
  selected: '',
  price: 0,
};

export default connect(mapStateToProps, mapDispatchToProps)(InsuranceContainer);
